import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, Typography, Paper, TextField, Button, MenuItem, 
  CircularProgress, Alert, Divider
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import SEO from '../components/SEO';
import { verticalsConfig } from '../config/servicesConfig';

// Make TS aware of Razorpay window object
declare global {
  interface Window {
    Razorpay: any;
  }
}

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const VerticalPaymentPage: React.FC = () => {
  const { verticalName } = useParams<{ verticalName: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const vertical = verticalName ? verticalsConfig[verticalName.toLowerCase()] : null;

  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadRazorpayScript();
    
    // Auto-select service from URL query params (e.g. ?service=student-reg)
    const searchParams = new URLSearchParams(location.search);
    const serviceParam = searchParams.get('service');
    if (serviceParam && vertical && vertical.services.find(s => s.id === serviceParam)) {
      setSelectedServiceId(serviceParam);
    }
  }, [location.search, vertical]);

  if (!vertical) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h4" color="error">Vertical Not Found</Typography>
      </Box>
    );
  }

  const selectedService = vertical.services.find(s => s.id === selectedServiceId);

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedService) {
      setError('Please select a service');
      return;
    }
    if (!name || !email || !phone) {
      setError('Please fill in all details');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // 1. Create order on our backend
      const orderResponse = await fetch('http://localhost:5000/api/payment/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: selectedService.priceINR,
          vertical: vertical.name,
          serviceSelected: selectedService.name,
          customerName: name,
          customerEmail: email,
          customerPhone: phone
        }),
      });

      const orderData = await orderResponse.json();

      if (!orderData.success) {
        throw new Error(orderData.message || 'Failed to create order');
      }

      // 2. Initialize Razorpay
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'YOUR_KEY_ID', // Replace via .env
        amount: orderData.order.amount,
        currency: 'INR',
        name: 'Octacrest Corporate',
        description: `Payment for ${selectedService.name} - ${vertical.name}`,
        order_id: orderData.order.id,
        handler: async function (response: any) {
          try {
            // 3. Verify Payment
            const verifyRes = await fetch('http://localhost:5000/api/payment/verify-payment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              })
            });
            const verifyData = await verifyRes.json();
            if (verifyData.success) {
              alert('Payment Successful!');
              navigate('/'); // redirect to success page later
            } else {
              alert('Payment Verification Failed!');
            }
          } catch (err) {
            console.error('Verify error', err);
            alert('Verification Error');
          }
        },
        prefill: {
          name: name,
          email: email,
          contact: phone,
        },
        theme: {
          color: '#48AFF0',
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response: any) {
        alert(response.error.description);
      });
      rzp.open();

    } catch (err: any) {
      console.error('Payment flow error', err);
      setError(err.message || 'An error occurred during payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', pt: 12, pb: 6, display: 'flex', justifyContent: 'center', background: '#0A0A0A' }}>
      <SEO title={`Checkout - ${vertical.name}`} description={`Secure checkout for ${vertical.name} services.`} url={`https://octacrestcorporate.com/pay/${verticalName}`} />
      
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 3, md: 5 }, 
          maxWidth: 650, 
          width: '100%', 
          background: 'rgba(25, 32, 38, 0.75)', 
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(72, 175, 240, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5), inset 0 0 20px rgba(72, 175, 240, 0.05)',
          color: '#fff', 
          borderRadius: '24px' 
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, color: '#00FFCC' }}>
          <LockOutlinedIcon sx={{ fontSize: 36, mr: 1.5 }} />
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '0.5px' }}>
            Secure Checkout
          </Typography>
        </Box>
        
        <Typography variant="subtitle1" sx={{ mb: 3, textAlign: 'center', color: '#A7B6C2', fontSize: '1.1rem' }}>
          {vertical.name} Services
        </Typography>

        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: 2, 
          p: 2, 
          mb: 4, 
          borderRadius: 2, 
          bgcolor: 'rgba(162, 89, 255, 0.1)', 
          border: '1px solid rgba(162, 89, 255, 0.3)' 
        }}>
          <SecurityIcon sx={{ color: '#A259FF', mt: 0.5 }} />
          <Typography variant="body2" sx={{ color: '#E0E8EF', lineHeight: 1.6 }}>
            <strong>Payment Security & Compliance:</strong> This transaction is securely processed and collected by <strong>Octacrest Corporate Pvt. Ltd.</strong> on behalf of <strong>{vertical.name}</strong>.
          </Typography>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mb: 4 }} />

        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}

        <form onSubmit={handlePayment}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField 
              select
              label="Select Service"
              value={selectedServiceId}
              onChange={(e) => setSelectedServiceId(e.target.value)}
              fullWidth
              required
              variant="outlined"
              sx={{ input: { color: '#fff' }, label: { color: '#A7B6C2' }, '.MuiOutlinedInput-root': { '& fieldset': { borderColor: '#48AFF0' }, '&:hover fieldset': { borderColor: '#00FFCC' }, color: '#fff' } }}
              SelectProps={{
                MenuProps: { PaperProps: { sx: { bgcolor: '#232b33', color: '#fff' } } }
              }}
            >
              <MenuItem value="" disabled>Select a service</MenuItem>
              {vertical.services.map((service) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name}
                </MenuItem>
              ))}
            </TextField>

            {selectedService && (
              <Box sx={{ 
                p: 3, 
                bgcolor: 'rgba(72,175,240,0.08)', 
                borderLeft: '4px solid #48AFF0',
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="subtitle1" sx={{ color: '#A7B6C2' }}>Total Payable</Typography>
                <Typography variant="h5" sx={{ color: '#00FFCC', fontWeight: 'bold' }}>
                  ₹{selectedService.priceINR.toLocaleString()}
                </Typography>
              </Box>
            )}

            <TextField 
              label="Full Name" 
              required 
              value={name} 
              onChange={e => setName(e.target.value)}
              sx={{ input: { color: '#fff' }, label: { color: '#A7B6C2' }, '.MuiOutlinedInput-root': { '& fieldset': { borderColor: '#48AFF0' } } }}
            />
            <TextField 
              label="Email Address" 
              type="email" 
              required 
              value={email} 
              onChange={e => setEmail(e.target.value)}
              sx={{ input: { color: '#fff' }, label: { color: '#A7B6C2' }, '.MuiOutlinedInput-root': { '& fieldset': { borderColor: '#48AFF0' } } }}
            />
            <TextField 
              label="Phone Number" 
              required 
              value={phone} 
              onChange={e => setPhone(e.target.value)}
              sx={{ input: { color: '#fff' }, label: { color: '#A7B6C2' }, '.MuiOutlinedInput-root': { '& fieldset': { borderColor: '#48AFF0' } } }}
            />

            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading || !selectedServiceId}
              sx={{ 
                mt: 2, 
                py: 1.5, 
                fontSize: '1.1rem',
                fontWeight: 'bold',
                background: 'linear-gradient(90deg, #A259FF 0%, #48AFF0 100%)',
                '&:hover': { opacity: 0.9 }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Pay Now'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default VerticalPaymentPage;
